/**
 * rAF loop with a fixed-step simulation (deterministic, network-friendly)
 * and a variable-rate render that receives the interpolation factor.
 * Also drives adaptive resolution from measured frame times.
 */
export interface LoopHandlers {
  step(dt: number): void;
  render(alpha: number, frameDt: number, time: number): void;
  /** Called once a second with the smoothed frame-time ratio vs the display refresh (1 = on budget) and that refresh interval (s). */
  load?(ratio: number, refresh: number): void;
}

export const TICK = 1 / 60;

/**
 * `maxFps` caps rendering (0 = every display frame); skipped vsyncs fold into the next frame's dt.
 * `setPaused(true)` stops stepping and rendering, as a hidden tab does: offline the game holds still,
 * online the sim catches up with the server's turns once running again.
 */
export function startLoop(h: LoopHandlers, maxFps = 0) {
  let last = performance.now(), acc = 0, time = 0, pending = 0, paused = false;
  const minInterval = maxFps > 0 ? 1 / maxFps : 0;
  // Refresh interval estimate (lowest smoothed delta we've seen) and load tracker.
  let refresh = 1 / 60, avg = 1 / 60, sinceLoad = 0;
  document.addEventListener('visibilitychange', () => { last = performance.now(); acc = 0; });
  const frame = (now: number) => {
    requestAnimationFrame(frame);
    const elapsed = (now - last) / 1000; last = now;
    if (document.hidden || paused) { acc = 0; pending = 0; return; }
    // A callback queued in the current frame can carry a timestamp earlier than boot.
    pending += Math.max(0, elapsed);
    // under a cap, wait for the next vsync at or after the target interval (2 ms tolerance for jitter)
    if (pending < minInterval - 0.002) return;
    const dt = Math.min(pending, 0.1); pending = 0;
    avg += (dt - avg) * 0.05;
    // track the display interval (lowest sustained delta); never assume slower than ~55 Hz
    refresh = Math.min(refresh * 1.0005, avg, 1 / 55, minInterval > 0 ? minInterval * 1.02 : 1);
    acc += dt;
    let n = 0;
    while (acc >= TICK && n < 8) { h.step(TICK); acc -= TICK; n++; }
    if (n === 8) acc = 0;
    time += dt;
    h.render(acc / TICK, dt, time);
    if ((sinceLoad += dt) > 1 && h.load) { sinceLoad = 0; h.load(avg / refresh, refresh); }
  };
  requestAnimationFrame(frame);
  return { setPaused(p: boolean) { paused = p; } };
}
